import React, { useState } from 'react';
import { Guest, InvitationType } from '../../_graphql/generated/types';
import { guestsApi, invitationsApi } from '../../_lib/api-client';
import InvitationTypeSelectionModal from '../InvitationTypeSelectionModal/InvitationTypeSelectionModal';

interface AddPlusOneModalProps {
  primaryGuest: Guest;
  eventId: string;
  invitationTypes: InvitationType[];
  onClose: () => void;
  onSuccess: () => void;
}

const AddPlusOneModal: React.FC<AddPlusOneModalProps> = ({ 
  primaryGuest, 
  eventId,
  invitationTypes,
  onClose, 
  onSuccess 
}) => {
  const [numberOfPlusOnes, setNumberOfPlusOnes] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInvitationTypeSelection, setShowInvitationTypeSelection] = useState(false);

  // Filter to only show invitation types with designs
  const typesWithDesigns = (invitationTypes || []).filter(type => 
    type.designCustomizations !== null && type.designCustomizations !== undefined
  );

  const handleCreate = async () => {
    // Check if invitation types are available
    if (typesWithDesigns.length === 0) {
      setError('No invitation types with designs available. Please design at least one invitation type first.');
      return;
    }

    // If there's only one invitation type, use it automatically
    if (typesWithDesigns.length === 1) {
      await createInvitationWithType(typesWithDesigns[0].id);
    } else {
      // Show the selection modal if there are multiple types
      setShowInvitationTypeSelection(true);
    }
  };

  const createInvitationWithType = async (invitationTypeId: string) => {
    setIsCreating(true);
    setError(null);

    try {
      // First create the plus ones
      const plusOnes = Array(numberOfPlusOnes).fill(null).map(() => ({
        // Empty objects since we don't require names for plus ones
      }));

      const createdPlusOnes = await guestsApi.addPlusOnes(primaryGuest.id, plusOnes);
      
      // Get all guest IDs (primary + plus ones)
      const allGuestIds = [primaryGuest.id];
      if (createdPlusOnes && Array.isArray(createdPlusOnes)) {
        allGuestIds.push(...createdPlusOnes.map(g => g.id));
      }

      // Create a group invitation for all guests
      const groupName = `${primaryGuest.name}${numberOfPlusOnes > 0 ? ` +${numberOfPlusOnes}` : ''}`;
      
      await invitationsApi.create({
        eventId,
        invitationTypeId,
        groupName,
        allowedPlusOnes: 0, // Already added the plus ones
        guestIds: allGuestIds
      });

      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create invitation');
    } finally {
      setIsCreating(false);
      setShowInvitationTypeSelection(false);
    }
  };

  const handleIncrement = () => {
    setNumberOfPlusOnes(prev => Math.min(prev + 1, 10)); // Max 10 plus ones at once
  };

  const handleDecrement = () => {
    setNumberOfPlusOnes(prev => Math.max(prev - 1, 1)); // Min 1 plus one
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setNumberOfPlusOnes(Math.max(1, Math.min(value, 10)));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 w-full max-w-md">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Add Plus Ones</h3>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Adding plus ones for <span className="font-medium text-gray-900">{primaryGuest.name}</span>
          </p>
          
          <div className="flex items-center justify-center gap-4">
            <span className="text-sm text-gray-600">Number of plus ones:</span>
            
            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                type="button"
                onClick={handleDecrement}
                disabled={numberOfPlusOnes <= 1 || isCreating}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              
              <input
                type="number"
                min="1"
                max="10"
                value={numberOfPlusOnes}
                onChange={handleInputChange}
                disabled={isCreating}
                className="w-16 px-2 py-2 text-center border-0 focus:outline-none focus:ring-0"
              />
              
              <button
                type="button"
                onClick={handleIncrement}
                disabled={numberOfPlusOnes >= 10 || isCreating}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-2">
            Names are not required for plus ones
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isCreating}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="px-4 py-2 text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isCreating ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating...
              </>
            ) : (
              <>Add {numberOfPlusOnes} Plus {numberOfPlusOnes === 1 ? 'One' : 'Ones'} & Create Invitation</>
            )}
          </button>
        </div>
      </div>
    </div>
    
    {/* Invitation Type Selection Modal */}
    <InvitationTypeSelectionModal
      isOpen={showInvitationTypeSelection}
      invitationTypes={typesWithDesigns}
      onSelect={createInvitationWithType}
      onCancel={() => setShowInvitationTypeSelection(false)}
      isCreating={isCreating}
    />
  </>
  );
};

export default AddPlusOneModal;